import { SupabaseClient } from '@supabase/supabase-js';
import { BaseRepository } from './base.repository';
import { UserProfile, UserProfileInsert, UserProfileUpdate } from '../entities/user';
import { PaginationOptions, PaginatedResponse } from '../types/pagination.types';
import { applyPagination } from '../utils/pagination.utils';

export class UserRepository extends BaseRepository<UserProfile, UserProfileInsert, UserProfileUpdate> {
  constructor(supabase: SupabaseClient) {
    super(supabase, 'users');
  }

  /**
   * Save a UserProfile entity (create or update based on dirty state)
   */
  async save(user: UserProfile): Promise<UserProfile> {
    if (!user.isDirty()) {
      return user; // No changes to save
    }

    const data = user.toData();
    const { id, created_at, ...updateData } = data; // Remove fields that shouldn't be updated

    const { data: saved, error } = await this.supabase
      .from(this.tableName)
      .update(updateData)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;

    // Create new entity instance from saved data
    const savedUser = UserProfile.fromData(saved);
    savedUser.markAsClean();
    return savedUser;
  }

  /**
   * Override findById to return UserProfile entity
   */
  async findById(id: string): Promise<UserProfile | null> {
    const { data, error } = await this.supabase
      .from(this.tableName)
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null; // Not found
      throw error;
    }
    
    return data ? UserProfile.fromData(data) : null;
  }

  /**
   * Override create to return UserProfile entity
   */
  async create(data: UserProfileInsert): Promise<UserProfile> {
    const { data: created, error } = await this.supabase
      .from(this.tableName)
      .insert(data)
      .select()
      .single();

    if (error) throw error;
    return UserProfile.fromData(created);
  }

  /**
   * Override update to return UserProfile entity
   */
  async update(id: string, data: UserProfileUpdate): Promise<UserProfile> {
    const { data: updated, error } = await this.supabase
      .from(this.tableName)
      .update(data)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return UserProfile.fromData(updated);
  }

  async findByEmail(email: string): Promise<UserProfile | null> {
    const { data, error } = await this.supabase
      .from(this.tableName)
      .select('*')
      .eq('email', email)
      .maybeSingle();

    if (error) throw error;
    return data ? UserProfile.fromData(data) : null;
  }

  async findByAuthId(authId: string): Promise<UserProfile | null> {
    const { data, error } = await this.supabase
      .from(this.tableName)
      .select('*')
      .eq('id', authId)
      .single();

    if (error && error.code !== 'PGRST116') throw error;
    return data ? UserProfile.fromData(data) : null;
  }

  async createOrUpdate(profile: UserProfileInsert): Promise<UserProfile> {
    const { data, error } = await this.supabase
      .from(this.tableName)
      .upsert(profile)
      .select()
      .single();

    if (error) throw error;
    return UserProfile.fromData(data);
  }

  async findByIds(ids: string[], options?: PaginationOptions): Promise<UserProfile[] | PaginatedResponse<UserProfile>> {
    const query = this.supabase
      .from(this.tableName)
      .select('*')
      .in('id', ids);

    return applyPagination<any, UserProfile>(query, options, (d) => UserProfile.fromData(d));
  }

  async updateAvatar(userId: string, avatarUrl: string): Promise<UserProfile> {
    const user = await this.findById(userId);
    if (!user) throw new Error('User not found');
    user.updateProfile(user.full_name, avatarUrl);
    return this.save(user);
  }

  async searchByName(searchTerm: string, options?: PaginationOptions): Promise<UserProfile[] | PaginatedResponse<UserProfile>> {
    const query = this.supabase
      .from(this.tableName)
      .select('*')
      .ilike('full_name', `%${searchTerm}%`);

    const finalOptions = {
      ...options,
      orderBy: options?.orderBy || 'full_name',
      order: options?.order || 'asc' as const,
      limit: options?.limit || 10
    };

    return applyPagination<any, UserProfile>(query, finalOptions, (d) => UserProfile.fromData(d));
  }

  async updateCurrentProject(userId: string, projectId: string | null): Promise<UserProfile> {
    const user = await this.findById(userId);
    if (!user) throw new Error('User not found');
    user.setCurrentProject(projectId);
    return this.save(user);
  }
}